import {
  AbstractMesh,
  ActionEvent,
  ActionManager,
  AnimationGroup,
  Color3,
  ExecuteCodeAction,
  GPUParticleSystem,
  IPhysicsCollisionEvent,
  Material,
  Mesh,
  MeshBuilder,
  ParticleSystem,
  PBRMaterial,
  PhysicsCharacterController,
  PhysicsEventType,
  PickingInfo,
  Quaternion,
  Ray,
  StandardMaterial,
  TrailMesh,
  TransformNode,
  Vector3,
} from '@babylonjs/core';
import { AssetObject } from './AssetObject';
import { MovingPlatformBehavior } from './behavior/MovingPlatformBehavior';
import { GameState } from './GameState';
import { Level } from './Level';
import { MaterialService } from './MaterialService';
import { MeshExplode } from './MeshExplode';
import { AssetObjectConfig, MeshFactory } from './MeshFactory';
import { PlayerCollision } from './PlayerCollision';

export class Player {
  // Movement & speed parameters
  private readonly playerHeight = 1.8;
  private readonly playerRadius = 0.4;
  private readonly gravityStrength = 20;
  private readonly fallGravityMultiplier = 1.5; // Makes falling faster than rising
  private readonly verticalVisualOffset = 0.12;
  private readonly projectileHitboxRadius = 1.0;
  private readonly aimOffset = new Vector3(0, 0.4, 0);
  private readonly dashDuration: number = 0.6;
  private readonly kickDuration: number = 0.6;
  private readonly shieldDuration: number = 5;
  private readonly throwCooldown: number = 0.75;

  private speed = 10;
  private jumpSpeed = 11;
  private backwardSpeed = 4;
  private turnSpeed = 10;
  private dashSpeed = 12;
  private projectileSpeed = 20;

  private dashTimer: number = 0;
  private kickTimer: number = 0;

  // Physics
  characterController!: PhysicsCharacterController;

  // Visual
  model!: TransformNode;
  displayMesh!: Mesh;
  debugCapsule!: Mesh;

  private hips: TransformNode | null = null;
  private dashDirection: Vector3 = Vector3.Zero();

  // Animations
  private runAnim!: AnimationGroup;
  private walkbackAnim!: AnimationGroup;
  private walkAnim!: AnimationGroup;
  private idleAnim!: AnimationGroup;
  private jumpAnim!: AnimationGroup;
  private jumprunningAnim!: AnimationGroup;
  private landAnim!: AnimationGroup;
  private dashAnim!: AnimationGroup;
  private climbLadderAnim!: AnimationGroup;
  private climbingladderAnim!: AnimationGroup;
  private dyingAnim!: AnimationGroup;
  private kickAnim!: AnimationGroup;
  private hitAnim!: AnimationGroup;
  private fallingAnim!: AnimationGroup;
  private idlehappyAnim!: AnimationGroup;
  private punchAnim!: AnimationGroup;
  private sambaAnim!: AnimationGroup;
  private throwAnim!: AnimationGroup;

  // Animation trackers
  private currentAnim!: AnimationGroup;
  private prevAnim!: AnimationGroup;

  // State flags
  public running: boolean = false;
  public falling: boolean = false;
  public jumping: boolean = false;
  public dashing: boolean = false;
  public isKicking: boolean = false;
  public isStomping: boolean = false;
  public grounded: boolean = false;
  public jumpRequested: boolean = false;
  public alive: boolean = true;
  public vulnerable: boolean = true;
  public isDying: boolean = false; // True while death animation is playing
  public isThrowing: boolean = false;
  public isDancing: boolean = false;
  public waitForInput: boolean = false;
  private throwCooldownTimer: number = 0;
  private projectiles: { root: Mesh; mesh: Mesh; trail: TrailMesh; velocity: Vector3; distance: number; maxDistance: number }[] = [];

  // stats
  public jumpCount: number = 1;
  public maxAirJumps: number = 1;
  public airJumpsLeft: number = 0;

  public ammunition: number = 10;
  public lives: number = 3;
  public coins: number = 0;
  public score: number = 0;
  public attempts: number = 1;
  public collectibles: number = 0;
  public enemies: number = 0;

  // Moving platform tracking (set by MovingPlatformBehavior)
  public currentPlatform: MovingPlatformBehavior | null = null;
  public currentPathIndex: number = 0;

  // Velocity tracking (manual for proper gravity)
  private verticalVelocity: number = 0;
  /** Persistent external velocity from behaviors (wind, magnet). Decays each frame. */
  private externalVelocity: Vector3 = Vector3.Zero();
  private externalVelocityDecay: number = 4; // decay rate per second (higher = faster decay)

  // Rotation tracking
  private targetRotation: number = 0;
  private currentRotation: number = 0;

  // Animation grounded smoothing (prevents flickering on slopes)
  private animationGrounded: boolean = false;
  private groundedGraceTimer: number = 0;
  private readonly groundedGraceTime: number = 0.4; // 400ms grace period

  // Ray for slope detection
  private slopeRay: Ray = new Ray(Vector3.Zero(), Vector3.Down());

  private assetRotation = new Vector3(Math.PI, Math.PI, 0);

  // ── Pre-allocated scratch objects to avoid per-frame GC allocations ──────
  private readonly scratchDown = new Vector3(0, -1, 0);
  private readonly scratchCamForward = new Vector3(0, 0, 1);
  private readonly scratchCamRight = new Vector3(1, 0, 0);
  private readonly scratchMoveDir = new Vector3(0, 0, 0);
  private readonly scratchDesiredVelocity = new Vector3(0, 0, 0);
  private readonly scratchModelQuat = new Quaternion(0, 0, 0, 1);
  private readonly scratchMeshQuat = new Quaternion(0, 0, 0, 1);
  // ─────────────────────────────────────────────────────────────────────────

  // Movement-particle state (avoid calling start/stop every frame)
  private movementParticlesRunning = false;

  private movementParticles: ParticleSystem | GPUParticleSystem;

  private projectileTrailMaterial: Material;
  private projectileColor: Color3 = new Color3(1, 0, 0);
  private projectileGravity = new Vector3(0, -15, 0); // Custom gravity for projectiles to feel good
  private projectileImpactMaterial: PBRMaterial;
  private projectileImpactDecalSize = new Vector3(2, 2, 2);
  private projectileImpactDecals: Mesh[] = [];
  private impactTarget: Mesh | null = null;
  private impactTargetMaterial: StandardMaterial | null = null;

  // Invincibility shield visualization
  private shield: Mesh | null = null;
  private shieldParticles: ParticleSystem | GPUParticleSystem | null = null;
  private shieldTimer: number = 0;

  constructor(private level: Level) {
    this.speed = this.level.config.player.speed;
    this.jumpSpeed = this.level.config.player.jumpSpeed;
    this.backwardSpeed = this.level.config.player.backwardSpeed;
    this.turnSpeed = this.level.config.player.turnSpeed;
    this.dashSpeed = this.level.config.player.dashSpeed;
    this.projectileSpeed = this.level.config.player.projectileSpeed;

    this.currentPathIndex = this.level.config.player.startPathIndex;

    const start = this.getStart();

    // Create physics character controller
    this.characterController = new PhysicsCharacterController(
      start,
      { capsuleHeight: this.playerHeight, capsuleRadius: this.playerRadius },
      this.level.scene
    );

    this.model = new TransformNode('playerRoot', this.level.scene);
    this.model.position = start.clone();
    this.model.rotationQuaternion = Quaternion.Identity();

    this.lives = this.level.game.user.lives;
    this.coins = this.level.game.user.coins;

    this.speed = this.level.config.player.autoRun ? this.speed * this.level.config.player.autoRun : this.speed;

    this.projectileTrailMaterial = this.level.game.materialService.createColorMaterial(
      'projectileTrailMaterial',
      this.projectileColor,
      0.3
    );
    this.projectileImpactMaterial = this.level.game.materialService.createPBRMaterial(
      'projectileImpactMaterial',
      'assets/particles/scorch_03.png'
    );
    this.projectileImpactMaterial.zOffset = -2;
    this.projectileImpactMaterial.albedoTexture.hasAlpha = true;
    this.projectileImpactMaterial.albedoColor = new Color3(0.5, 0, 0);
    this.projectileImpactMaterial.useAlphaFromAlbedoTexture = true;
    this.projectileImpactMaterial.roughness = 1;
    this.projectileImpactMaterial.metallic = 0;

    this.impactTargetMaterial = this.level.game.materialService.createColorMaterial('impactTargetMaterial', this.projectileColor, 0.7);
    this.impactTargetMaterial.backFaceCulling = false;
  }

  async init() {
    const rootMesh = AssetObjectConfig.builder().object(new AssetObject('player', 'Ch09')).build().buildMesh(this.level);

    rootMesh.parent = this.model;
    // Scale the model
    rootMesh.scaling.scaleInPlace(0.012);
    rootMesh.position = new Vector3(0, -this.playerHeight / 2 - this.verticalVisualOffset, 0);
    this.displayMesh = rootMesh as Mesh;
    // Initialize mesh rotation - rotate -90° on X to stand upright, and 180° on Y to face forward
    this.displayMesh.rotationQuaternion = Quaternion.FromEulerAngles(this.assetRotation.x, this.assetRotation.y, this.assetRotation.z);

    this.level.addShadowCaster(this.displayMesh);
    this.displayMesh.receiveShadows = true;
    this.displayMesh.checkCollisions = true;
    this.displayMesh.refreshBoundingInfo();

    // Find Hips to strip root motion - Get it from the skeleton since hierarchy might be flattened
    const asset = this.level.game.assetFactory.getAsset('player');
    const skeleton = asset?.getSkeleton();
    if (skeleton) {
      const hipsBone = skeleton.bones.find((b) => b.name.includes('Hips'));
      if (hipsBone) {
        this.hips = hipsBone.getTransformNode();
      }
    }

    this.configureAnimations();

    // Create debug capsule (hidden)
    this.debugCapsule = MeshBuilder.CreateCapsule(
      'playerCapsule',
      { height: this.playerHeight, radius: this.playerRadius },
      this.level.scene
    );
    this.debugCapsule.parent = this.model;
    this.debugCapsule.visibility = 0;
    this.debugCapsule.actionManager = new ActionManager(this.level.scene);

    for (const mesh of this.level.collisionMeshes) {
      this.registerCollisionMesh(mesh);
    }

    this.movementParticles = this.level.particles.createMovementParticleSystem(this.displayMesh);

    if (this.level.config.player.autoRun) {
      this.waitForInput = true;
    }

    this.lookAtNextPathPoint();
  }

  lookAtNextPathPoint() {
    const nextPathPoint = this.level.pathPoints[this.currentPathIndex + 2];
    if (nextPathPoint) {
      // Calculate direction to next path point
      const direction = nextPathPoint.subtract(this.model.position);

      // Calculate Y rotation from direction (atan2 uses x,z for horizontal plane rotation)
      const yRotation = Math.atan2(direction.x, direction.z);

      // Set both currentRotation and targetRotation so updateMovement doesn't override
      this.currentRotation = yRotation;
      this.targetRotation = yRotation;

      // Apply rotation immediately
      this.model.rotationQuaternion = Quaternion.FromEulerAngles(0, this.currentRotation, 0);
    }
  }

  /**
   * Register a collision mesh dynamically
   */
  registerCollisionMesh(mesh: AbstractMesh): void {
    if (!this.debugCapsule?.actionManager) {
      return;
    }

    this.debugCapsule.actionManager.registerAction(
      new ExecuteCodeAction(
        {
          trigger: ActionManager.OnIntersectionEnterTrigger,
          parameter: mesh,
        },
        (evt: ActionEvent) => {
          const playerCollision: PlayerCollision = mesh.metadata?.playerCollision;
          if (playerCollision?.action) {
            evt.sourceEvent = PhysicsEventType.COLLISION_STARTED;
            playerCollision.action(this.level, this, evt);
          }
        }
      )
    );

    this.debugCapsule.actionManager.registerAction(
      new ExecuteCodeAction(
        {
          trigger: ActionManager.OnIntersectionExitTrigger,
          parameter: mesh,
        },
        (evt: ActionEvent) => {
          const playerCollisionEnd: PlayerCollision = mesh.metadata?.playerCollisionEnd;
          if (playerCollisionEnd?.action) {
            evt.sourceEvent = PhysicsEventType.COLLISION_FINISHED;
            playerCollisionEnd.action(this.level, this, evt);
          }
        }
      )
    );
  }

  /**
   * Unregister a collision mesh - removes all actions associated with the mesh
   * Must be called before disposing meshes to prevent ghost collisions
   */
  unregisterCollisionMesh(mesh: AbstractMesh): void {
    if (!this.debugCapsule?.actionManager) {
      return;
    }

    const actionManager = this.debugCapsule.actionManager;

    // Find and remove all actions that reference this mesh
    // We need to iterate backwards since we're removing elements
    const actionsToRemove = actionManager.actions.filter((action) => {
      // Check if action trigger references this mesh as parameter
      return action.trigger === ActionManager.OnIntersectionEnterTrigger || action.trigger === ActionManager.OnIntersectionExitTrigger
        ? (action as any)._triggerParameter === mesh
        : false;
    });

    for (const action of actionsToRemove) {
      actionManager.unregisterAction(action);
    }
  }

  private configureAnimations(): void {
    // Get animations by name
    this.idleAnim = this.level.scene.getAnimationGroupByName('Idle')!;
    this.walkAnim = this.level.scene.getAnimationGroupByName('Walking')!;
    this.jumpAnim = this.level.scene.getAnimationGroupByName('JumpingUp')!;
    this.jumprunningAnim = this.level.scene.getAnimationGroupByName('RunningJump')!;
    this.climbLadderAnim = this.level.scene.getAnimationGroupByName('ClimbingLadder')!;
    this.landAnim = this.level.scene.getAnimationGroupByName('FallingToLanding')!;
    this.runAnim = this.level.scene.getAnimationGroupByName('Running')!;
    this.dashAnim = this.level.scene.getAnimationGroupByName('RunningSlide')!;
    this.walkbackAnim = this.level.scene.getAnimationGroupByName('WalkingBackward')!;
    this.climbingladderAnim = this.level.scene.getAnimationGroupByName('ClimbingLadder')!;
    this.dyingAnim = this.level.scene.getAnimationGroupByName('Dying')!;
    this.hitAnim = this.level.scene.getAnimationGroupByName('HitReaction')!;
    this.fallingAnim = this.level.scene.getAnimationGroupByName('FallingIdle')!;
    this.idlehappyAnim = this.level.scene.getAnimationGroupByName('HappyIdle')!;
    this.punchAnim = this.level.scene.getAnimationGroupByName('Punching')!;
    this.sambaAnim = this.level.scene.getAnimationGroupByName('SambaDancing')!;
    this.kickAnim = this.level.scene.getAnimationGroupByName('RoundKick')!;
    this.throwAnim = this.level.scene.getAnimationGroupByName('Throw')!;

    this.level.scene.stopAllAnimations();

    // looping
    this.runAnim.loopAnimation = true;
    this.walkAnim.loopAnimation = true;
    this.walkbackAnim.loopAnimation = true;
    this.idleAnim.loopAnimation = true;
    this.climbingladderAnim.loopAnimation = true;
    this.fallingAnim.loopAnimation = true;
    this.idlehappyAnim.loopAnimation = true;
    this.sambaAnim.loopAnimation = true;

    // speeds
    this.dyingAnim.speedRatio = 2;
    this.dashAnim.speedRatio = 1.5;
    this.throwAnim.speedRatio = 3;
    this.kickAnim.speedRatio = 2;

    // Enable blending for smooth transitions
    if (this.displayMesh && this.displayMesh.animations) {
      this.displayMesh.animations.forEach((animation) => {
        animation.enableBlending = true;
        animation.blendingSpeed = 0.05;
      });
    }

    // Initialize current and previous animations
    this.currentAnim = this.idleAnim;
    this.prevAnim = this.landAnim;

    if (this.currentAnim) {
      this.currentAnim.play(this.currentAnim.loopAnimation);
    }
  }

  update(deltaTime: number) {
    if (!this.displayMesh) {
      return;
    }

    // Check killing height - player fell off the map
    if (this.alive && !this.isDying && this.model.position.y < this.level.config.killingHeight) {
      this.die();
      return;
    }

    const support = this.characterController.checkSupport(deltaTime, this.scratchDown);
    const isSupported = support.supportedState !== 0;

    // If dying, just update position (no player input)
    if (this.isDying) {
      // Apply velocity to physics
      this.characterController.integrate(deltaTime, support, Vector3.Zero());
      // Sync model position
      this.model.position.copyFrom(this.characterController.getPosition());
      return;
    }

    // Don't update if dead
    if (!this.alive) {
      return;
    }

    // Update states
    this.updateState(isSupported, deltaTime);

    // Update movement
    this.updateMovement(deltaTime, support);

    // Update path index
    this.updatePathIndex();

    // Update animations
    this.animatePlayer();

    // Update throwing
    this.handleThrowing(deltaTime);
    this.updateImpactTarget(deltaTime);
    this.updateProjectiles(deltaTime);

    // Update invincibility shield animation
    this.updateShield(deltaTime);

    if (this.running && this.grounded && !this.dashing) {
      const soundName = this.level.inputManager.state.backward ? 'walkback' : 'run';
      this.level.game.sound.play(soundName);
    }
  }

  private updateState(isSupported: boolean, deltaTime: number): void {
    // Dash handling
    if (this.dashing) {
      this.dashTimer -= deltaTime;
      if (this.dashTimer <= 0) {
        this.dashing = false;
        this.dashTimer = 0;
      }
    }

    // Kick handling (air attack)
    if (this.isKicking) {
      this.kickTimer -= deltaTime;
      if (this.kickTimer <= 0) {
        this.isKicking = false;
        this.kickTimer = 0;
      }
    }

    // Stomp detection: player is airborne and falling fast enough
    // No extra input needed — landing on an enemy while falling triggers it
    this.isStomping = !this.grounded && this.verticalVelocity < -2.0;

    // Falling state - either from a jump going down, or walked off a platform
    // Guard: do NOT set falling here if already grounded - the sticky gravity (-2.0) would
    // otherwise always trigger this on slopes, fighting the ground-detection reset below.
    if (!this.grounded && this.verticalVelocity < -0.5) {
      this.falling = true;
    }

    // === PHYSICS GROUND DETECTION (controls movement, velocity) ===
    // We combine controller support check with a raycast to handle slopes better
    const slopeHit = this.checkGroundRaycast();

    // We are grounded if:
    // 1. Controller says we are supported (flat ground/shallow slope)
    // 2. OR Raycast hits ground very close (steep slope handling)
    // AND we are not moving upwards (jumping)
    const isGroundDetected = (isSupported || slopeHit) && this.verticalVelocity <= 0.1;

    if (isGroundDetected) {
      this.grounded = true;
      this.jumpCount = 1;
      this.airJumpsLeft = this.maxAirJumps;
      this.jumping = false;
      this.falling = false;

      // Apply "sticky" gravity to ensure we stay on the slope
      // Instead of 0, we use a small downward force
      this.verticalVelocity = -2.0;
    } else {
      this.grounded = false;
      // If not grounded and not jumping (walked off platform), set falling
      if (!this.jumping && this.verticalVelocity < 0) {
        this.falling = true;
      }
    }

    // === ANIMATION GROUNDED STATE (smoothed for slopes) ===
    // This prevents animation flickering on uneven terrain
    if (this.grounded) {
      // On ground - immediately set animation grounded
      this.animationGrounded = true;
      this.groundedGraceTimer = 0;
    } else if (this.animationGrounded && !this.jumping) {
      // Lost ground contact but was grounded - use grace timer
      this.groundedGraceTimer += deltaTime;
      if (this.groundedGraceTimer >= this.groundedGraceTime) {
        // Grace period expired - actually in air
        this.animationGrounded = false;
      }
      // During grace period, animationGrounded stays true
    } else {
      // In air (jumping or after grace period)
      this.animationGrounded = false;
      this.groundedGraceTimer = 0;
    }

    // Jump detection
    if (this.level.inputManager.state.jumpPressed && !this.dashing) {
      let shouldJump = false;

      let soundName = 'jump';
      if (this.jumpCount > 0) {
        this.jumpCount--;
        shouldJump = true;
      } else if (this.airJumpsLeft > 0) {
        soundName = 'airjump';
        this.airJumpsLeft--;
        this.level.particles.createAirJumpParticleSystem(this.displayMesh).start();
        shouldJump = true;
      }

      if (shouldJump) {
        this.jumpRequested = true;
        this.jumping = true;
        this.falling = false;
        this.grounded = false;
        this.animationGrounded = false;
        this.groundedGraceTimer = 0;

        this.level.game.sound.play(soundName);
      }
    }

    // Dash detection (Trigger) - only on ground
    // We only trigger if grounded, not currently dashing, and button is pressed
    if (this.grounded && !this.dashing && !this.jumping && !this.falling && this.level.inputManager.state.dash) {
      this.startDash();
    }

    // Kick detection (Trigger) - only in air
    // If in the air and dash button is pressed, do a kick instead
    if (!this.grounded && !this.isKicking && this.level.inputManager.state.dash) {
      this.startKick();
    }

    const wantParticles = this.grounded && this.running && !this.dashing;
    if (wantParticles && !this.movementParticlesRunning) {
      this.movementParticles.start();
      this.movementParticlesRunning = true;
    } else if (!wantParticles && this.movementParticlesRunning) {
      this.movementParticles.stop();
      this.movementParticlesRunning = false;
    }
  }

  private handleThrowing(deltaTime: number) {
    if (this.throwCooldownTimer > 0) {
      this.throwCooldownTimer -= deltaTime;
    }

    if (this.level.inputManager.state.throw && this.throwCooldownTimer <= 0 && this.ammunition > 0) {
      this.throwProjectile();
    }
  }

  private throwProjectile() {
    if (!this.throwAnim) return;

    this.ammunition--;
    this.throwCooldownTimer = this.throwCooldown;
    this.isThrowing = true;

    // Calculate animation duration
    // Default fps is often 60 in Babylon unless specified, but safe to assume 60 for these assets usually
    const frameCount = this.throwAnim.to - this.throwAnim.from;
    const effectiveFps = 60 * this.throwAnim.speedRatio;
    const durationMs = (frameCount / effectiveFps) * 1000;

    // Reset throwing state after animation duration
    setTimeout(() => {
      this.isThrowing = false;
    }, durationMs);

    // Spawn cannonball at 50% of animation
    setTimeout(() => {
      this.spawnProjectile();
    }, durationMs * 0.5);
  }

  private spawnProjectile() {
    // Check if player is still capable of throwing (e.g. not dead)
    if (!this.alive) return;

    this.level.game.sound.play('swipe');

    // Aim using camera direction if available, otherwise fallback to model forward
    const camera = this.level.scene.activeCamera;
    let aimDirection: Vector3;

    if (camera && !this.level.config.camera.orthographic) {
      // For ArcRotateCamera: direction from camera position through player
      const cameraPos = camera.position;
      const targetPos = this.model.position.add(new Vector3(0, 1.0, 0));
      aimDirection = targetPos.subtract(cameraPos).normalize();
      // Add slight upward angle for throw arc
      aimDirection = aimDirection.add(this.aimOffset).normalize();
    } else {
      aimDirection = this.model.forward.clone();
    }

    // Spawn position from player position (center mass)
    const spawnPos = this.model.position.add(new Vector3(0, 1.0, 0)).add(aimDirection.scale(0.5));

    // Create Root Mesh (handles position and movement, does NOT rotate)
    const root = new Mesh('projectileRoot', this.level.scene);
    root.position.copyFrom(spawnPos);

    // Create Visual Mesh (child of root, handles rotation)
    const asset = AssetObject.fromAsset('projectile', 4).withClone(true);
    const config = AssetObjectConfig.builder()
      .object(asset)
      .scale(asset.scale)
      .physicsShapeType(undefined)
      //.highlight(this.projectileColor)
      .build();
    const mesh = config.buildMesh(this.level) as Mesh;
    MeshFactory.configureMesh(mesh, config, this.level);

    mesh.checkCollisions = false;
    mesh.isPickable = false;

    // Parent visual mesh to root
    mesh.parent = root;
    mesh.position = Vector3.Zero(); // Local position relative to root

    root.checkCollisions = false;
    root.isPickable = false;

    // Create a specific non-rotating target for the trail
    // (using a tiny sphere to ensure TrailMesh has valid geometry to track, just in case)
    const trailTarget = MeshBuilder.CreateSphere('projectileTarget', { diameter: 0.1 }, this.level.game.scene);
    trailTarget.parent = root;
    trailTarget.position = Vector3.Zero();
    trailTarget.isVisible = false;

    // Initial velocity
    const velocity = aimDirection.scale(this.projectileSpeed);

    const projectile = {
      root: root,
      mesh: mesh,
      trail: null as TrailMesh | null,
      velocity: velocity,
      distance: 0,
      maxDistance: 50,
    };

    this.projectiles.push(projectile);

    // Create the trail after a short delay so it starts from the current projectile position
    setTimeout(() => {
      if (!root.isDisposed() && !trailTarget.isDisposed()) {
        const trail = new TrailMesh('projectileTrail', trailTarget, this.level.game.scene, 0.1, 10, true);
        trail.material = this.projectileTrailMaterial;
        projectile.trail = trail;
      }
    }, 50); // Wait ~3 frames for position to stabilize
  }

  private updateImpactTarget(deltaTime: number) {
    const aiming = this.level.inputManager.state.aim;

    // A) Cleanup when not aiming
    if (!aiming) {
      if (this.impactTarget) {
        this.impactTarget.isVisible = false;
      }
      return;
    }

    // B) Get aim direction from camera
    const camera = this.level.scene.activeCamera;
    let aimDirection: Vector3;

    if (camera) {
      const cameraPos = camera.position;
      const targetPos = this.model.position.add(new Vector3(0, 1.0, 0));
      aimDirection = targetPos.subtract(cameraPos).normalize();
      aimDirection = aimDirection.add(this.aimOffset).normalize();
    } else {
      aimDirection = this.model.forward.clone();
    }

    if (aimDirection.lengthSquared() < 0.01) {
      aimDirection = Vector3.Forward();
    }
    aimDirection.normalize();

    const startPosition = this.model.position.add(new Vector3(0, 1.0, 0)).add(aimDirection.scale(0.5));
    const startVelocity = aimDirection.scale(this.projectileSpeed);
    const gravity = this.projectileGravity;

    // C) Simulate trajectory to find impact point
    let impactPoint: Vector3 | null = null;
    let impactNormal: Vector3 = Vector3.Up();
    let simPos = startPosition.clone();
    let simVel = startVelocity.clone();
    const maxTime = 2.0;
    const checkStep = 0.05; // Larger steps = fewer raycasts (20 instead of 150)

    for (let t = 0; t < maxTime; t += checkStep) {
      simVel = simVel.add(gravity.scale(checkStep));
      const step = simVel.scale(checkStep);
      const nextPos = simPos.add(step);

      // Check for collision with ground/objects via raycast
      const ray = new Ray(simPos, step.normalize(), step.length() + 0.5);
      const hit = this.level.scene.pickWithRay(
        ray,
        (mesh) => {
          return mesh.metadata?.projectileCollision;
        },
        true
      );

      if (hit && hit.hit && hit.pickedPoint) {
        impactPoint = hit.pickedPoint;
        impactNormal = hit.getNormal(true) || Vector3.Up();
        break;
      }

      // Check if below killing height
      if (nextPos.y < this.level.config.killingHeight) {
        impactPoint = simPos;
        impactNormal = Vector3.Up();
        break;
      }

      simPos = nextPos;
    }

    // D) Show/update impact target
    if (impactPoint) {
      // Create impact target if not exists
      if (!this.impactTarget) {
        this.impactTarget = MeshBuilder.CreateDisc('impactTarget', { radius: 1, tessellation: 32 }, this.level.scene);
        this.impactTarget.isPickable = false;
        this.impactTarget.material = this.impactTargetMaterial;
      }

      // Position at impact point, slightly offset along normal to avoid z-fighting
      this.impactTarget.position.copyFrom(impactPoint.add(impactNormal.scale(0.05)));

      // Orient disc to face along the surface normal
      // The disc's default normal is +Z, we need to rotate it to match impactNormal
      const up = Vector3.Up();
      const forward = impactNormal;
      // Calculate rotation to align disc normal with surface normal
      if (Math.abs(Vector3.Dot(forward, up)) < 0.999) {
        const right = Vector3.Cross(up, forward).normalize();
        const adjustedUp = Vector3.Cross(forward, right).normalize();
        this.impactTarget.rotationQuaternion = Quaternion.FromLookDirectionLH(forward, adjustedUp);
      } else {
        // Surface is horizontal (ground or ceiling)
        this.impactTarget.rotationQuaternion = Quaternion.FromEulerAngles(forward.y > 0 ? -Math.PI / 2 : Math.PI / 2, 0, 0);
      }

      this.impactTarget.isVisible = true;
    } else if (this.impactTarget) {
      this.impactTarget.isVisible = false;
    }
  }

  private updateProjectiles(deltaTime: number) {
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const projectile = this.projectiles[i];

      // Apply Gravity
      projectile.velocity.addInPlace(this.projectileGravity.scale(deltaTime));

      // Move
      // Calculate step
      const step = projectile.velocity.scale(deltaTime);
      const length = step.length();
      const direction = step.normalizeToNew();

      // Check multiple rays for "thick" projectile effect
      // 1. Center
      // 2. Up, Down, Left, Right relative to movement
      const radius = this.projectileHitboxRadius;

      // Calculate Up and Right vectors relative to velocity
      // If moving straight up/down, use Forward as "Right", otherwise Cross with Up
      let right = Vector3.Cross(Vector3.Up(), direction);
      if (right.lengthSquared() < 0.001) {
        right = Vector3.Cross(Vector3.Forward(), direction);
      }
      right.normalize();
      const up = Vector3.Cross(direction, right).normalize();

      // Define offsets
      const offsets = [
        Vector3.Zero(), // Center
        up.scale(radius), // Top
        up.scale(-radius), // Bottom
        right.scale(radius), // Right
        right.scale(-radius), // Left
      ];

      let hitInfo: PickingInfo = null;

      for (const offset of offsets) {
        const origin = projectile.root.position.add(offset);
        const ray = new Ray(origin, direction, length);

        const hit = this.level.game.scene.pickWithRay(
          ray,
          (mesh) => {
            return mesh.metadata?.projectileCollision === true;
          },
          true
        );

        if (hit && hit.hit) {
          hitInfo = hit;
          break; // Stop at first hit
        }
      }

      if (hitInfo && hitInfo.hit) {
        const destroyable = hitInfo.pickedMesh.metadata?.projectileDestoyable === true;
        const disposable = hitInfo.pickedMesh.metadata?.projectileDisposable;
        const enemy = hitInfo.pickedMesh.metadata?.enemy;

        if (destroyable) {
          // Hit Destroyable
          const meshesToExplode = [hitInfo.pickedMesh, ...hitInfo.pickedMesh.getChildMeshes()];
          for (const m of meshesToExplode) {
            if (m instanceof Mesh && !m.isDisposed()) {
              this.level.particles.createDisappearanceParticleSystem(m, 0.4, MaterialService.colorRed);
              MeshExplode.explode(m, this.level);
            }
          }
          if (disposable) {
            disposable.dispose();
          }
          this.score += 100;
          this.enemies++;
          if (enemy) {
            this.level.game.sound.play('enemydestroy');
          }
        } else {
          // Hit Environment
          this.level.particles.createDisappearanceParticleSystem(projectile.mesh, 0.4, MaterialService.colorRed);

          const decal = MeshBuilder.CreateDecal('projectileImpactDecal', hitInfo.pickedMesh, {
            position: hitInfo.pickedPoint,
            normal: hitInfo.getNormal(true),
            size: this.projectileImpactDecalSize,
          });
          decal.material = this.projectileImpactMaterial;
          this.projectileImpactDecals.push(decal);
        }

        this.level.game.sound.play('projectilehit');

        //MeshExplode.explode(projectile.mesh, this.level, 2, 10, 0, 2, 2);

        // Dispose Projectile
        projectile.trail?.dispose();
        projectile.mesh.dispose();
        projectile.root.dispose();
        this.projectiles.splice(i, 1);

        continue;
      }

      // No hit, allow movement
      projectile.root.position.addInPlace(step);
      projectile.distance += length;

      // Rotate for visual effect
      projectile.mesh.rotation.x += 5 * deltaTime;

      // Dispose if max distance reached
      if (projectile.distance >= projectile.maxDistance || projectile.root.position.y < this.level.config.killingHeight) {
        projectile.trail?.dispose();
        projectile.mesh.dispose();
        projectile.root.dispose();
        this.projectiles.splice(i, 1);
        continue;
      }
    }
  }

  private startDash() {
    this.dashing = true;
    this.dashTimer = this.dashDuration;

    this.level.game.sound.play('swoosh');

    // Determine dash direction
    // If we are moving, dash in movement direction. Otherwise, dash forward.
    const input = this.level.inputManager.state;
    const hasInput = input.movementX !== 0 || input.movementY !== 0;

    if (hasInput && this.model.rotationQuaternion) {
      // Calculate world direction from input (same logic as in updateMovement)
      // We duplicates logic briefly here to get the precise intended direction

      const camera = this.level.scene.activeCamera;
      let camForward = new Vector3(0, 0, 1);
      let camRight = new Vector3(1, 0, 0);

      if (camera) {
        const cameraDir = camera.getDirection(Vector3.Forward());
        camForward = new Vector3(cameraDir.x, 0, cameraDir.z).normalize();
        camRight = Vector3.Cross(Vector3.Up(), camForward).normalize();
      }

      const inputX = input.movementX;
      const inputZ = input.movementY;

      const moveX = camForward.x * inputZ + camRight.x * inputX;
      const moveZ = camForward.z * inputZ + camRight.z * inputX;

      this.dashDirection = new Vector3(moveX, 0, moveZ).normalize();

      // Also update rotation immediately to face dash direction
      this.targetRotation = Math.atan2(this.dashDirection.x, this.dashDirection.z);
      this.currentRotation = this.targetRotation; // Snap rotation
      this.model.rotationQuaternion = Quaternion.FromEulerAngles(0, this.currentRotation, 0);
    } else {
      // Dash straight forward
      this.dashDirection = this.model.forward.clone();
    }
  }

  private startKick() {
    this.isKicking = true;
    this.kickTimer = this.kickDuration;

    this.level.game.sound.play('swoosh', undefined, { waitTime: 0.2 });
  }

  /**
   * Called after a successful stomp on an enemy.
   * Gives the player a small upward bounce impulse, like in Mario 64.
   */
  public stompBounce(): void {
    // Small bounce — about 55% of normal jump speed
    this.verticalVelocity = this.jumpSpeed * 0.55;
    this.jumping = true;
    this.grounded = false;
    this.falling = false;
    this.animationGrounded = false;
    this.groundedGraceTimer = 0;
    this.level.game.sound.play('jump');
  }

  /**
   * Apply an upward velocity force to the player, used by BouncePad and Geyser behaviors.
   * @param force upward velocity in units/sec (default jumpSpeed scale)
   */
  public applyUpwardForce(force: number): void {
    this.verticalVelocity = force;
    this.jumping = true;
    this.grounded = false;
    this.falling = false;
    this.animationGrounded = false;
    this.groundedGraceTimer = 0;
  }

  /**
   * Apply external velocity impulse to the player. The impulse is accumulated and injected into the character controller this frame.
   * @param impulse velocity impulse vector
   */
  public applyExternalImpulse(impulse: Vector3): void {
    this.externalVelocity.addInPlace(impulse);
  }

  private updateMovement(deltaTime: number, support: any): void {
    if (this.level.finished) {
      return;
    }

    const input = this.level.inputManager.state;

    // Get camera forward and right vectors for camera-relative movement
    const camera = this.level.scene.activeCamera;
    let camForward: Vector3;
    let camRight: Vector3;

    if (this.level.config.camera.orthographic && this.level.config.player.autoRun) {
      // For autoRun + orthographic, use world-aligned movement
      // Forward = +Z (along path), Right = +X (perpendicular to path)
      this.scratchCamForward.copyFromFloats(0, 0, 1);
      this.scratchCamRight.copyFromFloats(1, 0, 0);
      camForward = this.scratchCamForward;
      camRight = this.scratchCamRight;
    } else if (camera) {
      // Get camera forward direction (projected onto XZ plane)
      // Use camera.getDirection which is available on all cameras
      const cameraDir = camera.getDirection(Vector3.Forward());
      this.scratchCamForward.copyFromFloats(cameraDir.x, 0, cameraDir.z).normalize();
      Vector3.CrossToRef(Vector3.Up(), this.scratchCamForward, this.scratchCamRight);
      this.scratchCamRight.normalize();
      camForward = this.scratchCamForward;
      camRight = this.scratchCamRight;

      // For orthographic camera (without autoRun), compensate for orthographicAlpha offset
      // so that movement is aligned with the visual screen axes
      if (this.level.config.camera.orthographic) {
        const alphaOffset = this.level.config.camera.orthographicAlpha || 0;
        if (alphaOffset !== 0) {
          const cos = Math.cos(-alphaOffset);
          const sin = Math.sin(-alphaOffset);
          const newForwardX = camForward.x * cos - camForward.z * sin;
          const newForwardZ = camForward.x * sin + camForward.z * cos;
          this.scratchCamForward.copyFromFloats(newForwardX, 0, newForwardZ).normalize();
          Vector3.CrossToRef(Vector3.Up(), this.scratchCamForward, this.scratchCamRight);
          this.scratchCamRight.normalize();
        }
      }
    }

    // Use analog movement values for proportional speed control
    // movementX: left/right (-1 to 1)
    // movementY: forward/backward (-1 to 1)
    const inputX = input.movementX;

    if (this.waitForInput) {
      if (input.movementX !== 0 || input.movementY > 0 || input.jumpPressed || input.dash || input.throw) {
        this.waitForInput = false;
      }
    }

    const inputZ = this.level.config.player.autoRun ? (!this.waitForInput ? 1 : 0) : input.movementY;

    // Transform input to world direction using camera orientation
    let moveX = camForward.x * inputZ + camRight.x * inputX;
    let moveZ = camForward.z * inputZ + camRight.z * inputX;

    // Calculate input magnitude for proportional speed
    const inputMagnitude = Math.sqrt(inputX * inputX + inputZ * inputZ);
    const clampedMagnitude = Math.min(1, inputMagnitude); // Clamp to max 1

    // Determine base speed (slower when moving backward)
    let baseSpeed = this.speed;
    if (inputZ < -0.1 && Math.abs(inputX) < 0.3) {
      baseSpeed = this.backwardSpeed;
    }

    // Apply proportional speed based on stick/joystick tilt
    const speed = baseSpeed * clampedMagnitude;

    // Check if running (has significant movement)
    this.running = clampedMagnitude > 0.1;

    // Normalize diagonal movement direction (but keep magnitude for speed)
    // Calculate target rotation: facing movement direction
    this.scratchMoveDir.copyFromFloats(moveX, 0, moveZ);
    if (this.scratchMoveDir.lengthSquared() > 0) {
      this.scratchMoveDir.normalize();
      this.targetRotation = Math.atan2(this.scratchMoveDir.x, this.scratchMoveDir.z);
    }
    const moveDir = this.scratchMoveDir;

    // Horizontal velocity - apply proportional speed
    let horizontalVelocity: Vector3;

    if (this.dashing) {
      // While dashing, ignore input speed and use dash speed in fixed direction
      horizontalVelocity = this.dashDirection.scale(this.dashSpeed);
    } else {
      horizontalVelocity = moveDir.scale(speed);
    }

    // Check if on a moving platform (set by MovingPlatformBehavior)
    const onMovingPlatform = this.currentPlatform !== null && this.grounded;
    const platformInfo = onMovingPlatform ? this.currentPlatform!.getPlatformInfo() : null;

    // Handle vertical velocity
    if (this.jumpRequested) {
      this.jumpRequested = false;
      this.verticalVelocity = this.jumpSpeed;
      // Clear platform when jumping
      this.currentPlatform = null;
    } else if (onMovingPlatform) {
      // On a moving platform - don't apply gravity, keep velocity at 0
      // We'll handle Y position directly
      this.verticalVelocity = 0;
    } else if (!this.grounded) {
      // In air - apply gravity (with multiplier when falling for snappier jumps)
      const gravityMultiplier = this.verticalVelocity < 0 ? this.fallGravityMultiplier : 1.0;
      this.verticalVelocity -= this.gravityStrength * gravityMultiplier * deltaTime;
    } else {
      // Grounded on static platform or slope
      // Apply sticky force to keep player grounded on slopes
      this.verticalVelocity = -2.0;
    }

    // Combine velocities
    this.scratchDesiredVelocity.copyFromFloats(horizontalVelocity.x, this.verticalVelocity, horizontalVelocity.z);
    let desiredVelocity = this.scratchDesiredVelocity;

    // Add platform horizontal velocity if on moving platform
    if (onMovingPlatform && platformInfo) {
      desiredVelocity.x += platformInfo.velocity.x;
      desiredVelocity.z += platformInfo.velocity.z;
    }

    desiredVelocity.addInPlace(this.externalVelocity);

    if (this.externalVelocity.lengthSquared() > 0.0001) {
      const decay = Math.max(0, 1 - this.externalVelocityDecay * deltaTime);
      this.externalVelocity.scaleInPlace(decay);
    } else {
      this.externalVelocity.set(0, 0, 0);
    }

    // Apply velocity
    this.characterController.setVelocity(desiredVelocity);
    this.characterController.integrate(deltaTime, support, Vector3.Zero());

    // Get new position
    let newPosition = this.characterController.getPosition();

    // If on a moving platform, ALWAYS set Y to platform top
    // This is the simplest way to ensure player stays on the platform
    if (onMovingPlatform && platformInfo) {
      const targetY = platformInfo.topY + this.playerHeight / 2;
      newPosition = new Vector3(newPosition.x, targetY, newPosition.z);
      this.characterController.setPosition(newPosition);
    }

    this.model.position.copyFrom(newPosition);

    // FORCE MESH TO STAY IN PLACE
    // The dash animation (and possibly others) has root motion that moves the mesh away from center.
    // We strictly enforce the mesh position relative to the physics capsule here.
    this.displayMesh.position.copyFromFloats(0, -this.playerHeight / 2 - this.verticalVisualOffset, 0);

    // Strip Root Motion from Hips (keep Y for bobbing/crouching, kill X/Z)
    if (this.hips) {
      this.hips.position.x = 0;
      this.hips.position.z = 0;
    }

    // Update model rotation to face movement direction
    if (this.running && !this.dashing) {
      const rotationDiff = this.targetRotation - this.currentRotation;
      let shortestDiff = ((rotationDiff + Math.PI) % (2 * Math.PI)) - Math.PI;
      if (shortestDiff < -Math.PI) shortestDiff += 2 * Math.PI;

      this.currentRotation += shortestDiff * Math.min(1, this.turnSpeed * deltaTime);
    }

    // Apply rotation to model (absolute, not incremental)
    Quaternion.FromEulerAnglesToRef(0, this.currentRotation, 0, this.scratchModelQuat);
    this.model.rotationQuaternion!.copyFrom(this.scratchModelQuat);

    // Handle backward walking - base mesh rotation includes +90° X to stand upright
    // For backward walking, we want mesh to face -Z (toward camera)
    // Handle backward walking
    if (!this.level.config.player.autoRun && input.backward && !input.forward && !input.left && !input.right) {
      // Walking purely backward - only X rotation to stand upright
      Quaternion.FromEulerAnglesToRef(this.assetRotation.x, 0, this.assetRotation.z, this.scratchMeshQuat);
    } else {
      // Normal forward facing - X rotation to stand + Y rotation PI to face +Z
      Quaternion.FromEulerAnglesToRef(this.assetRotation.x, this.assetRotation.y, this.assetRotation.z, this.scratchMeshQuat);
    }
    this.displayMesh.rotationQuaternion!.copyFrom(this.scratchMeshQuat);
  }

  private animatePlayer(): void {
    const input = this.level.inputManager.state;
    const isMovingForward = (this.level.config.player.autoRun && !this.waitForInput) || input.forward || input.left || input.right;
    const isMovingBackward = !this.level.config.player.autoRun && input.backward && !input.forward && !input.left && !input.right;

    if (isMovingForward) {
      this.throwAnim.speedRatio = 7;
    } else {
      this.throwAnim.speedRatio = 3;
    }

    // Determine animation based on state
    // Use animationGrounded (smoothed) for all ground/air decisions to prevent flickering on slopes
    // animationGrounded = true means we're on ground (or in grace period)
    // animationGrounded = false means we're actually in the air

    if (this.dashing) {
      // Dashing on ground
      this.currentAnim = this.dashAnim;
    } else if (this.isKicking) {
      // Kicking in air
      this.currentAnim = this.kickAnim;
    } else if (this.isDancing) {
      this.currentAnim = this.sambaAnim;
    } else if (this.isThrowing) {
      this.currentAnim = this.throwAnim;
    } else if (this.animationGrounded && !this.jumping && isMovingForward) {
      // Running on ground
      this.currentAnim = this.runAnim;
    } else if (this.animationGrounded && !this.jumping && isMovingBackward) {
      // Walking backward on ground
      this.currentAnim = this.walkbackAnim;
    } else if (this.jumping && this.verticalVelocity > 0) {
      // Actively jumping upward
      this.currentAnim = isMovingForward ? this.jumprunningAnim || this.jumpAnim : this.jumpAnim;
    } else if (this.animationGrounded && !this.jumping) {
      // Standing idle on ground
      this.currentAnim = this.idleAnim;
    } else if (!this.animationGrounded) {
      // In the air (falling or descending from jump)
      this.currentAnim = this.landAnim;
    }

    // Play animation if changed
    if (this.currentAnim && this.prevAnim !== this.currentAnim) {
      if (this.prevAnim) {
        this.prevAnim.stop();
      }

      // Special handling for DASH and KICK (play once)
      if (this.currentAnim === this.dashAnim) {
        this.dashAnim.loopAnimation = false;
        this.dashAnim.play(false);
      } else if (this.currentAnim === this.kickAnim) {
        this.kickAnim.loopAnimation = false;
        this.kickAnim.play(false);
      } else if (this.currentAnim === this.throwAnim) {
        this.throwAnim.loopAnimation = false;
        this.throwAnim.play(false);
      } else {
        this.currentAnim.play(this.currentAnim.loopAnimation);
      }

      this.prevAnim = this.currentAnim;
    }
  }

  public playSamba() {
    this.level.scene.stopAllAnimations();
    if (this.sambaAnim) {
      this.isDancing = true;
      this.currentAnim = this.sambaAnim;
      this.sambaAnim.play(true);
    }
  }

  private updatePathIndex() {
    const points = this.level.pathPoints;
    if (!points || points.length === 0) return;

    // Search within a local window around the current index so we do O(window)
    // instead of O(n) every frame. Window of ±20 is large enough to handle
    // fast movement without missing the closest point.
    const windowSize = 20;
    const start = Math.max(0, this.currentPathIndex - windowSize);
    const end = Math.min(points.length - 1, this.currentPathIndex + windowSize);

    let closestDistance = Number.MAX_VALUE;
    let closestIndex = this.currentPathIndex;
    const playerPos = this.model.position;

    for (let i = start; i <= end; i++) {
      const dist = Vector3.DistanceSquared(playerPos, points[i]);
      if (dist < closestDistance) {
        closestDistance = dist;
        closestIndex = i;
      }
    }

    this.currentPathIndex = closestIndex;
  }

  /**
   * Casts a ray downwards to check for ground even if physics controller loses contact for a frame
   * This helps with slope handling where the capsule might momentarily lose contact
   */
  private checkGroundRaycast(): boolean {
    // Update ray origin to player center
    this.slopeRay.origin.copyFrom(this.model.position);

    // Length: half height + margin. Player height is 1.8, so half is 0.9.
    // We add 0.5m margin for slopes.
    this.slopeRay.length = this.playerHeight / 2 + 0.5;

    const pick = this.level.scene.pickWithRay(this.slopeRay, (mesh) => {
      // Pick collision-enabled meshes, exclude player parts
      return mesh.checkCollisions && mesh !== this.displayMesh && mesh !== this.debugCapsule;
    });

    return pick ? pick.hit : false;
  }

  /**
   * Creates the Fresnel shield sphere mesh with glowing edges
   */
  private createShield(): Mesh {
    const shield = MeshBuilder.CreateSphere('invincibilityShield', { diameter: 2.5, segments: 32 }, this.level.scene);
    shield.parent = this.model;
    shield.position.y = 0; // Center on player
    const shieldMaterial = this.level.game.materialService.createGlassMaterial(this.level.scene.environmentTexture);
    shield.material = shieldMaterial;
    shield.isPickable = false;
    this.level.highlights.addMesh(shield, Color3.White(), true);
    return shield;
  }

  /**
   * Starts the invincibility visual effect (shield + particles)
   */
  startShield(): void {
    // Create shield mesh
    if (!this.shield) {
      this.shield = this.createShield();
    }
    this.shield.setEnabled(true);
    this.shieldTimer = 0;

    // Create particle ring around the shield sphere
    if (!this.shieldParticles) {
      this.shieldParticles = this.level.particles.createShieldParticleRing(this.shield!);
    }
    this.shieldParticles.start();

    // Set up auto-stop after invulnerable duration
    setTimeout(() => {
      this.stopShield();
      this.vulnerable = true;
    }, this.shieldDuration * 1000);
  }

  /**
   * Stops and cleans up the invincibility visual effect
   */
  private stopShield(): void {
    // Create burst effect before hiding the shield
    if (this.shield && this.shield.isEnabled()) {
      this.level.particles.createShieldBurstEffect(this.model.position.clone());
    }

    if (this.shield) {
      this.shield.setEnabled(false);
    }

    if (this.shieldParticles) {
      this.shieldParticles.stop();
    }

    this.level.game.sound.play('shieldoff');
  }

  /**
   * Updates the invincibility shield animation (pulsing)
   */
  private updateShield(deltaTime: number): void {
    if (!this.shield || !this.shield.isEnabled()) {
      return;
    }

    this.shieldTimer += deltaTime;

    // Pulsing scale animation: oscillate between 1.0 and 1.15
    const pulseSpeed = 2; // Speed of pulsation
    const pulseAmount = 0.1; // Max scale variation
    const baseScale = 1.0;
    const pulse = baseScale + pulseAmount * Math.sin(this.shieldTimer * pulseSpeed);
    this.shield.scaling.setAll(pulse);
  }

  /**
   * Called when player collides with something
   * @param event
   * @param message
   */
  collide(event: ActionEvent | IPhysicsCollisionEvent, message?: string) {
    if (this.alive && this.vulnerable && !this.level.game.config.godMode) {
      if (!message) {
        message = 'You crashed!';
      }
      this.level.game.sound.play('damage');
      this.level.hud.showMessage(message, 1000);
      this.level.particles.createDeathParticleSystem(this.model.position.clone());
      this.die();
    }
  }

  die() {
    if (!this.alive || this.level.game.config.sceneViewer) {
      return; // Already resetting
    }
    this.setAlive(false);
    this.looseLife();
  }

  setAlive(alive: boolean) {
    this.alive = alive;
  }

  getStart(): Vector3 {
    const pathinfo = this.level.getPathInfo(this.level.config.player.startPathIndex);
    const start = pathinfo.pathPoint.add(this.level.config.player.startOffset);
    return start;
  }

  private respawn() {
    // Reset player to start position
    let startPos = this.getStart();

    // Check for last checkpoint
    const checkpointPos = this.level.checkpointGenerator.getLastActivatedCheckpointPosition();
    if (checkpointPos) {
      startPos = checkpointPos;
      // Ensure we spawn a bit up to avoid stuck in floor if pivot is weird
      startPos.y += 1;
    }

    this.characterController.setPosition(startPos);
    this.model.position.copyFrom(startPos);
    this.verticalVelocity = 0;
    this.jumping = false;
    this.isKicking = false;
    this.isDancing = false;
    this.isThrowing = false;
    this.falling = false;
    this.dashing = false;
    this.grounded = true;
    this.currentPlatform = null;
    this.jumpCount = 1;
    this.vulnerable = false;
    this.externalVelocity.set(0, 0, 0);
    if (this.level.config.player.autoRun) {
      this.waitForInput = true;
    }

    this.updatePathIndex();
    this.lookAtNextPathPoint();

    // Reset camera to follow player at start position
    if (this.level.cameraController) {
      this.level.cameraController.resetToPlayer();
    }

    // Start invincibility visual effect (handles timer and vulnerable reset)
    this.startShield();
  }

  looseLife() {
    if (this.lives > 0) {
      this.lives--;
    }

    if (this.level.timeLimit < 2) {
      this.level.timeLimit = this.level.config.timeLimit;
    }

    this.attempts++;
    this.isDying = true;

    this.characterController.setVelocity(Level.fullGravity);

    // Enable slow motion instead of freezing velocity
    //this.level.setTimeScale(0.5); // 2x slower

    const underKillingHeight = this.model.position.y < this.level.config.killingHeight;

    // Choose death animation based on grounded state at time of death
    const deathAnim = underKillingHeight ? this.fallingAnim : this.dyingAnim;

    const message = this.lives + ' lives left';

    this.level.hud.showMessage(message, 1000, true);
    this.level.game.sound.speak(message);

    this.level.game.sound.play('lifelost');

    // Stop current animation
    if (this.currentAnim) {
      this.currentAnim.stop();
    }

    // Play death animation (non-looping)
    deathAnim.loopAnimation = false;
    deathAnim.play(false);

    // Wait for animation to complete, then reset position
    deathAnim.onAnimationEndObservable.addOnce(() => {
      //this.level.resetTimeScale(); // Reset slow motion
      this.respawn();
      this.isDying = false;
      if (this.lives > 0) {
        this.setAlive(true);
        // Resume idle animation
        if (this.idleAnim) {
          this.currentAnim = this.idleAnim;
          this.idleAnim.play(true);
        }
      } else {
        // Defer state change to next frame to avoid issues during scene disposal
        setTimeout(() => this.level.game.routeTo(GameState.OVER), 0);
      }
    });
  }

  /**
   * Get distance from a point to player
   */
  getDistance(position: Vector3): number {
    return Vector3.Distance(position, this.getPosition());
  }

  getProgress(): number {
    return (this.currentPathIndex / this.level.pathPoints.length) * 100.0;
  }

  getPosition(): Vector3 {
    return this.characterController.getPosition();
  }

  dispose() {
    this.model.dispose();
  }
}
